import { Injectable } from "@angular/core";
import { BackupJobData, DevicesFilter, JobExecutionStatus, SchedulerJob } from "@nms-ng2/app/modules/scheduler/scheduler.models";
import { BackupDevicesFilter } from "./create-backup-modal/tabs/backup-devices-tab/backup-devices-tab.model";
import { MatchingRules, Rule } from "@nms-ng2/app/shared/components/elements/matching-rules/matching-rules.models";
import { EquipmentSelectionType } from "@nms-ng2/app/modules/template/template-instance/template-instance-models";
import { MatchingRulesUtilsService } from "@nms-ng2/app/shared/components/elements/matching-rules/matching-rules-utils.service";
import { BackupDevicesScheduler } from "./create-backup-modal/tabs/backup-scheduler-tab/backup-scheduler-tab.model";
import { BackupDevicesGeneral } from "./create-backup-modal/tabs/backup-general-tab/backup-general-tab.model";
import { NmsWizardStepMapData } from "@nms-angular-toolkit/nms-wizard";

/**
 * Utilitário para tratar dados da tarefa de backup de equipamentos.
 */
@Injectable({
    providedIn: "root"
})
export class DeviceBackupUtilsService {

    constructor(private matchingRulesUtils: MatchingRulesUtilsService) { }

    async buildDevicesFilter(schedulerJob: SchedulerJob): Promise<BackupDevicesFilter> {
        const backupJobData: BackupJobData = (schedulerJob.jobData as BackupJobData);
        const model: BackupDevicesFilter = new BackupDevicesFilter();

        model.devicesFilter = backupJobData.devicesFilter;
        model.matchingRules = (model.devicesFilter.equipmentSelectionType === EquipmentSelectionType.FILTER)
                            ? this.buildRulesFromJob(model.devicesFilter)
                            : this.matchingRulesUtils.initMatchingRules();

        return model;
    }

    async buildDeviceBackupTriggers(schedulerJob: SchedulerJob): Promise<BackupDevicesScheduler> {
        const model = new BackupDevicesScheduler();

        model.jobType  = schedulerJob.type;
        model.id       = schedulerJob.id;
        model.triggers = schedulerJob.triggers;

        return model;
    }

    async buildDeviceBackupGeneral(schedulerJob: SchedulerJob): Promise<BackupDevicesGeneral> {
        const backupJobData = schedulerJob.jobData as BackupJobData;
        const backupGeneral = new BackupDevicesGeneral();

        backupGeneral.description   = backupJobData.description;
        backupGeneral.name          = schedulerJob.name;
        backupGeneral.enabled       = schedulerJob.enabled;
        backupGeneral.exportBackups = backupJobData.exportBackups;
        backupGeneral.status        = schedulerJob.status || JobExecutionStatus.NOT_REQUESTED

        return backupGeneral;
    }

    async buildInitBackupGeneral(): Promise<BackupDevicesGeneral> {
        const backupGeneral = new BackupDevicesGeneral();

        backupGeneral.enabled = true;

        return backupGeneral;
    }

    /**
     * Preenche os dados de edição do backup
     * @param schedulerJob - dados do agendamento
     * @returns - mapa com os dados das wizards.
     */
    async fillTabsData(schedulerJob: SchedulerJob): Promise<NmsWizardStepMapData> {
        const wizardStepsCacheMap: NmsWizardStepMapData = {};
        const [ devicesFilter, backupTriggers, backupGeneral ] = await Promise.all([
            this.buildDevicesFilter(schedulerJob),
            this.buildDeviceBackupTriggers(schedulerJob),
            this.buildDeviceBackupGeneral(schedulerJob)
        ]);

        wizardStepsCacheMap["BackupDevicesFilter"] = devicesFilter;
        wizardStepsCacheMap["BackupDevicesScheduler"] = backupTriggers;
        wizardStepsCacheMap["BackupDevicesGeneral"] = backupGeneral;

        return wizardStepsCacheMap;
    }

    /**
     * Preenche valores default com os dados da página de scheduler e da página geral do wizard
     * @param schedulerJob - dados do agendamento
     * @returns - mapa com os dados das wizards.
     */
    async initDefaultValues(schedulerJob: SchedulerJob) {
        const wizardStepsCacheMap: NmsWizardStepMapData = {};
        const [ backupTriggers, backupGeneral ] = await Promise.all([
            this.buildDeviceBackupTriggers(schedulerJob),
            this.buildInitBackupGeneral()
        ]);

        wizardStepsCacheMap["BackupDevicesScheduler"] = backupTriggers;
        wizardStepsCacheMap["BackupDevicesGeneral"] = backupGeneral;

        return wizardStepsCacheMap;
    }

    private buildRulesFromJob(devicesFilter: DevicesFilter): MatchingRules {
        const rules: Rule[] = devicesFilter.deviceFilterRules
            .map<Rule>((rule) =>
                ({
                    ruleType: rule.ruleType,
                    values: rule.values,
                    field: rule.deviceField
                } as Rule)
            );

        return {
            criteria: devicesFilter.rulesCriteria,
            rules: rules,
            useRules: true
        };
    }
}
